import { RootState } from "@/app/store";
import { RecordingState } from "@/common/utils/types";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

interface GlobalState {
    completing: boolean;
    globalRecordingState: RecordingState;
    // EN-837 (finding #1): "recorder busy because unpersisted audio is uploading" must survive
    // OVERLAPPING uploads. When this was a single boolean, one upload's completion cleared it while a
    // second upload was still in flight (e.g. a manual FileUploadButton concurrent with a
    // recording-triggered upload) — a false-idle that lets a deferred destructive teardown (sign-out,
    // version reload, router swap) fire mid-upload and lose the audio, the exact loss this guards
    // against. We track the SET of in-flight upload keys (transcript-section ids) instead; a settle
    // removes only its own key, so another upload's claim survives. `isUploadingGlobal` is the derived
    // mirror (`length > 0`) that every reader already consumes — kept in sync by the reducers below so
    // no reader changes. Removal is idempotent, so one upload settling on more than one exit path
    // (tus onError → reject → outer catch) is harmless.
    uploadingSectionIds: string[];
    isUploadingGlobal: boolean; // derived: uploadingSectionIds.length > 0 — do not set directly
    // EN-837 (finding #2): bridges the browser-transport stop→upload gap. `stopRecording` dispatches
    // globalRecordingState=inactive SYNCHRONOUSLY, but the just-recorded clip's `isUploadingGlobal`
    // only flips true a macrotask later (terminal `ondataavailable` → uploadAudio). `finalizing` is
    // set at stop and cleared once the upload takes over, so the recorder-idle guard never sees a
    // false-idle tick in between. Bounded (no unpersisted-audio risk — the incremental IndexedDB
    // snapshots persist the audio), so the watchdog MAY release it, unlike `isUploadingGlobal`.
    //
    // Keyed by transcript-section id (same as uploads) so overlapping recordings can't clobber each
    // other's bridge: if a second recording stops while the first's terminal `ondataavailable` is
    // still in flight, the first's clear removes only its OWN key. Verified safe: the active
    // recorder's closure id always equals `transcriptSectionIdRef.current` at the arm site (the hourly
    // rollover reassigns the ref AND starts a new recorder whose closure carries the new id), so
    // arm-id == both clear-ids; the rollover-stop and extension-transport clears target unarmed keys
    // and are idempotent no-ops. `finalizing` is the derived mirror (`length > 0`) every reader consumes.
    finalizingSectionIds: string[];
    finalizing: boolean; // derived: finalizingSectionIds.length > 0 — do not set directly
}

const initialState: GlobalState = {
    completing: false,
    globalRecordingState: RecordingState.inactive,
    uploadingSectionIds: [],
    isUploadingGlobal: false,
    finalizingSectionIds: [],
    finalizing: false,
};

export const isDisabled = (state: GlobalState): boolean => {
    return (
        state.isUploadingGlobal ||
        state.globalRecordingState === RecordingState.recording ||
        state.globalRecordingState === RecordingState.paused
    );
};
export const selectGlobalState = (state: RootState): GlobalState => state.globalState;

export const selectIsDisabled = createSelector([selectGlobalState], (globalState) => isDisabled(globalState));

const globalStateSlice = createSlice({
    name: "globalState",
    initialState,
    reducers: {
        setCompleting(state, action: PayloadAction<boolean>) {
            state.completing = action.payload;
        },
        setGlobalRecordingState(state, action: PayloadAction<RecordingState>) {
            state.globalRecordingState = action.payload;
        },
        // EN-837 (finding #1): a specific upload (keyed by transcript-section id) is now in flight.
        // Idempotent — re-arming the same key is a no-op.
        uploadStarted(state, action: PayloadAction<string>) {
            if (!state.uploadingSectionIds.includes(action.payload)) {
                state.uploadingSectionIds.push(action.payload);
            }
            state.isUploadingGlobal = state.uploadingSectionIds.length > 0;
        },
        // A specific upload finished (any terminal path). Removing an absent key is a no-op, so
        // multiple settles for one upload, or a settle before a matching start, are both safe.
        uploadSettled(state, action: PayloadAction<string>) {
            state.uploadingSectionIds = state.uploadingSectionIds.filter((id) => id !== action.payload);
            state.isUploadingGlobal = state.uploadingSectionIds.length > 0;
        },
        // EN-837 (finding #2): the stop→upload bridge for a specific recording is armed. Idempotent.
        finalizingStarted(state, action: PayloadAction<string>) {
            if (!state.finalizingSectionIds.includes(action.payload)) {
                state.finalizingSectionIds.push(action.payload);
            }
            state.finalizing = state.finalizingSectionIds.length > 0;
        },
        // The bridge for a specific recording is released (upload took over, or the terminal event
        // ended). Removing an absent key is a no-op, so the rollover-stop / extension clears are safe.
        finalizingSettled(state, action: PayloadAction<string>) {
            state.finalizingSectionIds = state.finalizingSectionIds.filter((id) => id !== action.payload);
            state.finalizing = state.finalizingSectionIds.length > 0;
        },
        // EN-837: clear every recorder busy-flag at once. Dispatched when the router swaps to the
        // unauthenticated tree on a SOFT (no-reload) sign-out — the in-memory store survives that
        // swap, so a flag the watchdog released while still true (a timed-out completing/finalizing
        // hold) would otherwise persist into the next authenticated session on the same tab and
        // wrongly disable Sign Out / defer teardowns. A hard-nav sign-out reloads and resets the
        // store to initialState, so this is only load-bearing for the soft-swap path.
        resetRecorderFlags(state) {
            state.completing = false;
            state.globalRecordingState = RecordingState.inactive;
            state.uploadingSectionIds = [];
            state.isUploadingGlobal = false;
            state.finalizingSectionIds = [];
            state.finalizing = false;
        },
    },
});

export const {
    setCompleting,
    setGlobalRecordingState,
    uploadStarted,
    uploadSettled,
    finalizingStarted,
    finalizingSettled,
    resetRecorderFlags,
} = globalStateSlice.actions;

export default globalStateSlice.reducer;
